import type { Router } from "vue-router";
import { loadLocaleNamespace } from "@/locales/loader";
import { systemStorage } from "@/utils/storage";
import { trackPageView } from "@/utils/fetch";

// 路由守卫，用于按需加载语言包
export const setupI18Guard = (router: Router) => {
  router.beforeEach(async (to, from, next) => {
    const lang = systemStorage.value.lang || "en";

    const namespace = (to.meta.i18Alias as string) || (to.name as string);
    if (namespace) {
      try {
        await loadLocaleNamespace(lang, namespace);
      } catch (error) {
        console.warn(`Failed to load locale namespace: ${namespace}`, error);
      }
    }
    next();
  });
};

// 需要统计的页面名称列表
const trackedPages = [
  "home",
  "report",
  "pricing",
  "tickets",
  "reports",
  "billing-orders",
  "billing-invoices",
];

/**
 * 页面访问跟踪路由守卫
 */
export const setupTrackGuard = (router: Router) => {
  router.beforeEach(async (to, from, next) => {
    // 检查当前页面是否在需要统计的列表中
    const pageName = to.name as string;
    if (trackedPages.includes(pageName)) {
      // 异步执行页面统计，不阻塞路由跳转
      trackPageView(pageName).catch(error => {});
    }

    next();
  });
};

/**
 * 路由守卫
 */
export function setupGuard(router: Router) {
  router.beforeEach(async (to, from, next) => {
    const needLogin = customMenus?.some(menu => menu.key === to.name) || !!to.meta.requiresAuth;
    const userStore = useUserStore();
    const { isLoggedIn } = toRefs(userStore);

    if (needLogin && !isLoggedIn.value) {
      window.dispatchEvent(
        new CustomEvent("unauthorized", {
          detail: { url: to.fullPath },
        })
      );
      next();
      return;
    }

    // 登录状态检查和重定向
    // 注册页面如果已登录也重定向到仪表盘
    // 第三方认证页面如果已登录重定向到仪表盘
    if (["login", "register", "auth"].includes(to.name as string) && isLoggedIn.value) {
      next("/dashboard");
      return;
    }

    // 1. 普通页面直接放行
    if (!to.meta.requiresAdmin) return next();

    // 2. 管理员页面 → 必须是 admin
    const adminStore = useAdminStore();
    const { isAdminLoggedIn } = toRefs(adminStore);

    if (isAdminLoggedIn.value || ["OpsForbidden", "OpsLogin"].includes(to.name as string))
      return next();

    const ADMIN_HASH = import.meta.env.VITE_ADMIN_HASH;

    // 如果已登录管理员账户，检查是否需要重定向到完整路径
    if (isAdminLoggedIn.value) {
      // 如果访问的是不带前缀的仪表盘路径，重定向到带前缀的路径
      if ((to.name as string)?.startsWith("Ops")) {
        return next(`/internal/ops/${ADMIN_HASH}/${to.path}`);
      }
      return next();
    }
    next(`/internal/ops/${ADMIN_HASH}/ops-login?redirect=` + encodeURIComponent(to.fullPath));
  });
}
